/**
 * Pure bundle type definitions + JSON validator. Split out of
 * serialize.ts so it can be unit-tested without dragging in the
 * PWA's Solid stores (which touch localStorage and would need a
 * shim in Node).
 *
 * Anything that produces or consumes a bundle envelope shape lives
 * here. The build/import functions that touch the live stores stay
 * in serialize.ts and re-export from this module.
 */

import type { Project } from '../layout/projectStore.ts';
import type { MacroLibrary, SequencerLibrary, MatrixLibrary, TimelineLibrary, RigStateLibrary } from '@opbuttons/schema';
import type { BridgeConfig } from '../opbridge/storage.ts';

/** Schema version for the bundle envelope. Bump if the bundle layout
 *  (not the contained library shapes) changes incompatibly. */
export const BUNDLE_SCHEMA = 'opbuttons-bundle/v1' as const;

interface BundleMeta {
  schema: typeof BUNDLE_SCHEMA;
  exportedAt: number;
  origin: string;
  kind: 'project' | 'all';
}

/* The fields marked "since 2026-10-01" (plan 3.2, "exports include
   everything") are OPTIONAL: a bundle written before them has none and still
   imports, and an older build ignores them. Additive, so the envelope schema
   string is unchanged. */

export interface ProjectBundle extends BundleMeta {
  kind: 'project';
  project: Project;
  macros: MacroLibrary;
  sequencers: SequencerLibrary;
  /** Since 2026-10-01: the timelines this project owns or points at. */
  timelines?: TimelineLibrary;
}

export interface AllBundle extends BundleMeta {
  kind: 'all';
  projects: Project[];
  macros: MacroLibrary;
  sequencers: SequencerLibrary;
  matrix: MatrixLibrary;
  /** Since 2026-10-01: every timeline, each with its `projectId`. */
  timelines?: TimelineLibrary;
  /** Since 2026-10-01: the Rig State library. */
  rigStates?: RigStateLibrary;
  /** Since 2026-10-01: this device's bridge pairings, BEARERS INCLUDED - the
   *  file can reach those OPBridges, which is why the export says to keep it
   *  private. A lighting setup (fixtures, patch, groups) lives on OPBridge, not
   *  in the browser, so restoring the pairing is what brings it back. */
  pairings?: BridgeConfig[];
  /** Since 2026-10-01: allow-listed per-device preferences, raw stored
   *  strings by key (`restoreMerge.ts::RESTORABLE_SETTING_KEYS`). */
  settings?: Record<string, string>;
}

export type Bundle = ProjectBundle | AllBundle;

export type BundleValidation =
  | { ok: true; bundle: Bundle }
  | { ok: false; reason: string };

/** Sniff + minimally validate a parsed JSON value. Doesn't deep-verify
 *  every field (the project store's migrator handles defaulting), but
 *  catches the obvious "wrong file dropped in" cases up-front so the
 *  import dialog can fail fast with a clear message. */
export function validateBundle(parsed: unknown): BundleValidation {
  if (typeof parsed !== 'object' || parsed === null) {
    return { ok: false, reason: 'Not a JSON object.' };
  }
  const obj = parsed as Record<string, unknown>;
  if (obj.schema !== BUNDLE_SCHEMA) {
    return { ok: false, reason: `Wrong schema (expected ${BUNDLE_SCHEMA}, got ${String(obj.schema)}).` };
  }
  if (obj.kind === 'project') {
    if (typeof obj.project !== 'object' || obj.project === null) {
      return { ok: false, reason: 'Project bundle missing `project` field.' };
    }
    if (typeof obj.macros !== 'object' || typeof obj.sequencers !== 'object') {
      return { ok: false, reason: 'Project bundle missing libraries.' };
    }
    return { ok: true, bundle: obj as unknown as ProjectBundle };
  }
  if (obj.kind === 'all') {
    if (!Array.isArray(obj.projects)) {
      return { ok: false, reason: 'All bundle missing `projects` array.' };
    }
    if (
      typeof obj.macros !== 'object'
      || typeof obj.sequencers !== 'object'
      || typeof obj.matrix !== 'object'
    ) {
      return { ok: false, reason: 'All bundle missing libraries.' };
    }
    return { ok: true, bundle: obj as unknown as AllBundle };
  }
  return { ok: false, reason: `Unknown bundle kind: ${String(obj.kind)}.` };
}

/** Parse a JSON string and validate. Returns ok=false on JSON parse
 *  errors too. */
export function parseBundle(text: string): BundleValidation {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    return { ok: false, reason: `Invalid JSON: ${(e as Error).message}` };
  }
  return validateBundle(parsed);
}
